import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";

import "./ViewJobPage.css";

const API_BASE_URL = process.env.REACT_APP_BACKEND_API_URL;

// Helper to format dates
const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const options = { year: "numeric", month: "short", day: "numeric" };
  return new Date(dateStr).toLocaleDateString(undefined, options);
};

export default function ViewJobPage() {
  const navigate = useNavigate();
  const { jobId } = useParams();

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchJob = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API_BASE_URL}/jobs/${jobId}`);
        if (res.data) setJob(res.data);
      } catch (err) {
        console.error("Failed to fetch job:", err.message);
        setError("Failed to load job data.");
      } finally {
        setLoading(false);
      }
    };
    fetchJob();
  }, [jobId]);

  if (loading) return <div className="view-job-page">Loading job data…</div>;
  if (error) return <div className="view-job-page view-job-error">{error}</div>;
  if (!job) return <div className="view-job-page">Job not found.</div>;

  return (
    <div className="view-job-page">
      <h1 className="view-job-title">{job.title}</h1>

      {/* ACTION BUTTONS */}
      <div className="view-job-actions">
        <motion.button
          className="view-job-btn"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => navigate(-1)}
        >
          &larr; Back
        </motion.button>

        <motion.button
          className="view-job-btn"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => window.print()}
        >
          Print
        </motion.button>
      </div>

      {/* JOB DETAILS */}
      <div className="view-job-card">
        <p><strong>Company:</strong> {job.company}</p>
        <p><strong>Location:</strong> {job.location}</p>
        <p><strong>Type:</strong> {job.type}</p>
        <p><strong>Deadline:</strong> {formatDate(job.deadline)}</p>
        <p><strong>Posted By:</strong> {job.postedBy}</p>
        {job.editedBy && (
          <p>
            <strong>Last Edited By:</strong> {job.editedBy} on {formatDate(job.dateEdited)}
          </p>
        )}

        <hr className="view-job-divider" />

        <div className="view-job-section">
          <h3>Summary</h3>
          <p>{job.summary}</p>
        </div>

        <div className="view-job-section">
          <h3>Description</h3>
          <p>{job.description}</p>
        </div>

        <div className="view-job-section">
          <h3>Responsibilities</h3>
          <ul>
            {job.responsibilities.map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
        </div>

        <div className="view-job-section">
          <h3>Requirements</h3>
          <ul>
            {job.requirements.map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
        </div>

        <div className="view-job-section">
          <h3>Apply</h3>
          {job.applyType === "external" && job.applyLink ? (
            <a href={job.applyLink} target="_blank" rel="noreferrer">{job.applyLink}</a>
          ) : (
            <p>Apply internally</p>
          )}
        </div>
      </div>
    </div>
  );
}
